import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import DOMPurify from 'dompurify';
import { markdownText } from '../general/markdown';

export interface CatalogueMetadataRecord {
    key: string;
    name: string;
    description: string;
    descriptionIsHtml?: boolean;
}

function catalogueDescription(record: CatalogueMetadataRecord): string {
    const text = record.descriptionIsHtml
        ? new DOMParser().parseFromString(DOMPurify.sanitize(record.description.replace(/<\/(?:p|h[1-6]|li|div)>|<br\s*\/?\s*>/gi, ' '),
            { ALLOWED_TAGS: [], ALLOWED_ATTR: [] }), 'text/html').body.textContent ?? ''
        : markdownText(record.description);
    const normalized = text.replace(/\s+/g, ' ').trim();
    if (!normalized) return `Explore ${record.name} from Triad Teacher Training.`;
    if (normalized.length <= 160) return normalized;
    const excerpt = normalized.slice(0, 157);
    const wordEnd = excerpt.lastIndexOf(' ');
    return `${wordEnd > 100 ? excerpt.slice(0, wordEnd) : excerpt}...`;
}

const defaultMetadata = {
    title: 'English teacher training | Triad Teacher Training',
    description: 'English teacher training with Olha Popivnenko: CELTA preparation, DELTA Module 1 preparation, DELTA Module 3 teaching methodology products.',
};

const publicMetadata: Record<string, { title: string; description: string }> = {
    '/': defaultMetadata,
    '/products': {
        title: 'Teacher training products | Triad Teacher Training',
        description: 'Browse English teacher training products from Triad Teacher Training, including Pre-CELTA preparation and teaching methodology. Compare descriptions and prices.',
    },
    '/pricing': {
        title: 'Product pricing | Triad Teacher Training',
        description: 'Compare prices for Triad Teacher Training products. View teacher training products and one-time prices in GBP, including applicable taxes.',
    },
    '/services': {
        title: 'CELTA preparation & DELTA supervision | Triad Teacher Training',
        description: 'CELTA preparation, DELTA Module 1 exam preparation and DELTA Module 3 teaching. Explore teacher consultations and book with Olha Popivnenko.',
    },
    '/about': {
        title: 'About Olha Popivnenko | Triad Teacher Training',
        description: 'Meet Olha Popivnenko, CELTA trainer and founder of Triad Teacher Training, offering DELTA Module 1 preparation, Module 3 teacher development.',
    },
    '/reviews': {
        title: 'Product reviews | Triad Teacher Training',
        description: 'Read reviews from teachers who have completed products from Triad Teacher Training.',
    },
    '/terms-of-use': { title: 'Terms & conditions | Triad Teacher Training', description: 'Read the website terms of use.' },
    '/privacy-policy': { title: 'Privacy policy | Triad Teacher Training', description: 'Learn how Triad Teacher Training uses account, learning, purchase information.' },
    '/refund-policy': { title: 'Refund & cancellation policy | Triad Teacher Training', description: 'Read the course purchase refund policy.' },
};

export function PageMetadata({ catalogueRecord }: { catalogueRecord?: CatalogueMetadataRecord | null }) {
    const { pathname, search } = useLocation();
    const recordKey = catalogueRecord?.key;
    const recordName = catalogueRecord?.name;
    const recordDescription = catalogueRecord?.description;
    const descriptionIsHtml = catalogueRecord?.descriptionIsHtml;
    const ownsCatalogueMetadata = catalogueRecord !== undefined;
    useEffect(() => {
        const path = pathname.replace(/\/+$/, '').toLowerCase() || '/';
        const selectionParameter = path === '/products' ? 'course' : path === '/services' ? 'service' : null;
        // Catalogue pages own their metadata because they already load and validate the selected record.
        // The global instance handles other routes without duplicating catalogue API requests.
        if (selectionParameter && !ownsCatalogueMetadata) return;
        const selectedKey = selectionParameter
            ? new URLSearchParams(search).getAll(selectionParameter).find(Boolean) : null;
        const validRecord = selectionParameter && selectedKey && recordKey === selectedKey && recordName;
        const metadata = validRecord
            ? { title: `${recordName} | Triad Teacher Training`, description: catalogueDescription({
                key: recordKey!, name: recordName!, description: recordDescription ?? '', descriptionIsHtml
            }) }
            : path.startsWith('/certificate/')
            ? { title: 'Certificate of completion | Triad Teacher Training', description: 'View a shared Triad Teacher Training course completion certificate.' }
            : publicMetadata[path] ?? defaultMetadata;
        document.title = metadata.title;
        let description = document.head.querySelector<HTMLMetaElement>('meta[name="description"]');
        if (!description) {
            description = document.createElement('meta');
            description.name = 'description';
            document.head.appendChild(description);
        }
        description.content = metadata.description;

        let canonical = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
        if (publicMetadata[path]) {
            if (!canonical) {
                canonical = document.createElement('link');
                canonical.rel = 'canonical';
                document.head.appendChild(canonical);
            }
            const canonicalSearch = validRecord ? `?${new URLSearchParams({ [selectionParameter!]: recordKey! }).toString()}` : '';
            canonical.href = `https://popivnenko.com${path}${canonicalSearch}`;
        } else {
            canonical?.remove();
        }
        return () => { canonical?.remove(); };
    }, [pathname, search, ownsCatalogueMetadata, recordKey, recordName, recordDescription, descriptionIsHtml]);
    return null;
}
